import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, LayoutGrid, Send, Clock, Pencil, Palette, Layers,
  Users, Search, GraduationCap, CalendarDays, Repeat, ClipboardList,
} from "lucide-react";
import { getArea, formatDMY } from "@/components/parent/shared";

const DIFF = { Kolay: "bg-emerald-100 text-emerald-700", Orta: "bg-amber-100 text-amber-700", Zor: "bg-red-100 text-red-700" };
const ASSIGN_STATUS = {
  assigned: { label: "Atandı", cls: "bg-slate-100 text-slate-600" },
  submitted: { label: "Veli Gönderdi", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Onaylandı", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-700" },
};
const ALL = "__all__";
const NO_PARENT = "__no_parent__";
const emptyTask = { title: "", description: "", area: "", difficulty: "Kolay", duration: "", image_url: "" };

function TaskCategoryManager({ categories, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#7C3AED" });
  const [saving, setSaving] = useState(false);
  const [forceDel, setForceDel] = useState(null);

  const openCreate = () => { setEditing(null); setForm({ name: "", color: "#7C3AED" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color: c.color || "#7C3AED" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Kategori adı zorunlu"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/task-categories/${editing.id}`, form);
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/task-categories", form);
        toast.success("Kategori eklendi");
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (cat, force) => {
    try {
      await api.delete(`/task-categories/${cat.id}${force ? "?force=true" : ""}`);
      toast.success("Kategori silindi");
      setForceDel(null);
      reload();
    } catch (err) {
      if (err.response?.status === 409) {
        setForceDel({ cat, detail: formatApiErrorDetail(err.response?.data?.detail) });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
  };

  return (
    <div data-testid="task-category-manager">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Ev ödevi kategorilerini yönetin. Ad değişikliği mevcut ödevlere yansır.</p>
        <Button onClick={openCreate} data-testid="add-task-category-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700">
          <Plus className="mr-1.5 h-4 w-4" /> Kategori Ekle
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((c) => (
          <Card key={c.id} data-testid={`task-category-card-${c.id}`} className="flex items-center gap-3 rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <span className="h-8 w-8 flex-shrink-0 rounded-xl" style={{ backgroundColor: c.color }} />
            <span className="min-w-0 flex-1 truncate font-heading font-semibold text-slate-900">{c.name}</span>
            <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-task-category-${c.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => doDelete(c, false)} data-testid={`delete-task-category-${c.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
          </Card>
        ))}
        {categories.length === 0 && (
          <Card className="col-span-full rounded-2xl border-dashed p-14 text-center text-slate-400">
            <Layers className="mx-auto mb-3 h-9 w-9 text-slate-300" />Henüz kategori yok.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="task-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Kategori Düzenle" : "Yeni Kategori"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Kategori Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="task-category-name-input" placeholder="Örn: Dil ve İletişim" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Palette className="h-4 w-4 text-violet-500" /> Renk</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="task-category-color-input" className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" />
                <span className="text-sm text-slate-500">{form.color}</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="task-category-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!forceDel} onOpenChange={(o) => !o && setForceDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="force-delete-task-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi ödevleriyle sil?</AlertDialogTitle>
            <AlertDialogDescription>
              {forceDel?.detail || "Bu kategoride ev ödevleri var."} Onaylarsanız kategori ve bağlı tüm ev ödevleri silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDelete(forceDel.cat, true)} data-testid="confirm-force-delete-task-category" className="rounded-xl bg-red-600 hover:bg-red-700">
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AssignedTasksPanel({ students, areaColor }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assignments");
        setAssignments(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    const ensure = (key, name) => {
      if (!map.has(key)) map.set(key, { key, name, students: [], items: [] });
      return map.get(key);
    };
    const label = (parentId, parentName) => {
      if (parentName) return parentName;
      return parentId ? "Adı bilinmeyen veli" : "Veli atanmamış";
    };
    for (const s of students) {
      const g = ensure(s.parent_id || NO_PARENT, label(s.parent_id, s.parent_name));
      if (s.full_name && !g.students.includes(s.full_name)) g.students.push(s.full_name);
    }
    for (const a of assignments) {
      const g = ensure(a.parent_id || NO_PARENT, label(a.parent_id, a.parent_name));
      if (a.student_name && !g.students.includes(a.student_name)) g.students.push(a.student_name);
      g.items.push(a);
    }
    return [...map.values()]
      .map((g) => ({
        ...g,
        items: g.items.slice().sort((x, y) => (y.created_at || "").localeCompare(x.created_at || "")),
      }))
      .sort((x, y) => y.items.length - x.items.length || x.name.localeCompare(y.name, "tr"));
  }, [students, assignments]);

  useEffect(() => {
    if (!selectedKey && groups.length) setSelectedKey(groups[0].key);
  }, [groups, selectedKey]);

  const q = query.trim().toLocaleLowerCase("tr");
  const visible = q
    ? groups.filter(
        (g) =>
          g.name.toLocaleLowerCase("tr").includes(q) ||
          g.students.some((s) => s.toLocaleLowerCase("tr").includes(q)),
      )
    : groups;
  const selected = groups.find((g) => g.key === selectedKey) || null;

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;
  }

  return (
    <div data-testid="assigned-tasks-panel">
      <p className="mb-4 text-sm text-slate-600">
        Veli seç ve o veliye bugüne kadar verdiğin tüm ev ödevlerini gör.
      </p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] lg:col-span-1">
          <div className="mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-violet-500" />
            <h3 className="font-heading text-base font-semibold text-slate-800">Veliler</h3>
            <span className="ml-auto text-xs font-medium text-slate-400">{groups.length}</span>
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Veli veya öğrenci ara"
              data-testid="assigned-search-input"
              className="h-10 rounded-xl pl-9"
            />
          </div>

          <div className="max-h-[32rem] space-y-2 overflow-y-auto pr-1">
            {visible.map((g) => {
              const active = g.key === selectedKey;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setSelectedKey(g.key)}
                  data-testid={`assigned-parent-${g.key}`}
                  className={`w-full rounded-xl border p-3 text-left transition-colors ${
                    active
                      ? "border-violet-500 bg-violet-50"
                      : "border-slate-200 bg-white hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className={`min-w-0 truncate font-semibold ${active ? "text-violet-700" : "text-slate-800"}`}>
                      {g.name}
                    </span>
                    <Badge className={`flex-shrink-0 rounded-full ${g.items.length ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`}>
                      {g.items.length}
                    </Badge>
                  </div>
                  {g.students.length > 0 && (
                    <p className="mt-1 truncate text-xs text-slate-500">{g.students.join(", ")}</p>
                  )}
                </button>
              );
            })}
            {visible.length === 0 && (
              <p className="py-8 text-center text-sm text-slate-400">Eşleşen veli yok.</p>
            )}
          </div>
        </Card>

        <div className="lg:col-span-2">
          {!selected ? (
            <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500">
              <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />Henüz veli kaydı yok.
            </Card>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{selected.name}</h3>
                  {selected.students.length > 0 && (
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                      <GraduationCap className="h-3.5 w-3.5" />
                      {selected.students.join(", ")}
                    </p>
                  )}
                </div>
                <Badge className="rounded-full bg-violet-100 text-violet-700">
                  {selected.items.length} ödev
                </Badge>
              </div>

              {selected.items.length === 0 ? (
                <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="assigned-tasks-empty">
                  <ClipboardList className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                  Bu veliye henüz ev ödevi vermedin.
                </Card>
              ) : (
                <div className="space-y-3">
                  {selected.items.map((a) => {
                    const st = ASSIGN_STATUS[a.status] || ASSIGN_STATUS.assigned;
                    const area = a.task?.area;
                    const hex = areaColor(area);
                    const sentCount = (a.feedback || []).filter((f) => f.comment || f.media_urls?.length).length;
                    return (
                      <Card
                        key={a.id}
                        data-testid={`assigned-task-${a.id}`}
                        className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                        style={area ? { borderLeft: `4px solid ${hex}` } : {}}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h4 className="font-heading text-base font-semibold text-slate-900">
                            {a.task?.title || "Silinmiş ödev"}
                          </h4>
                          <Badge className={`rounded-full ${st.cls}`}>{st.label}</Badge>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {area && (
                            <Badge className="rounded-full border-0 hover:opacity-90" style={{ backgroundColor: `${hex}1f`, color: hex }}>
                              {area}
                            </Badge>
                          )}
                          {a.task?.difficulty && (
                            <Badge className={`rounded-full ${DIFF[a.task.difficulty] || DIFF.Kolay}`}>{a.task.difficulty}</Badge>
                          )}
                          {a.student_name && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <GraduationCap className="h-3 w-3" />{a.student_name}
                            </span>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Send className="h-3 w-3" />Verildi: {formatDMY(a.created_at)}
                          </span>
                          {(a.start_date || a.end_date) && (
                            <span className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {formatDMY(a.start_date)} — {formatDMY(a.end_date)}
                            </span>
                          )}
                          {a.frequency && (
                            <span className="flex items-center gap-1"><Repeat className="h-3 w-3" />{a.frequency}</span>
                          )}
                          {a.task?.duration && (
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{a.task.duration}</span>
                          )}
                        </div>

                        {a.note && (
                          <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">Aile notu: {a.note}</p>
                        )}

                        {sentCount > 0 && (
                          <p className="mt-2 text-xs font-medium text-emerald-600">
                            Veli {sentCount} geri bildirim gönderdi
                          </p>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [students, setStudents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTask);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const [assignFor, setAssignFor] = useState(null);
  const [aForm, setAForm] = useState({ student_id: "", frequency: "Her Gün", start_date: "", end_date: "", note: "" });
  const [assigning, setAssigning] = useState(false);
  const [areaFilter, setAreaFilter] = useState(ALL);

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/task-categories");
      setCategories(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const load = async () => {
    try {
      const [t, s, c] = await Promise.all([
        api.get("/tasks"),
        api.get("/students"),
        api.get("/task-categories"),
      ]);
      setTasks(t.data);
      setStudents(s.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const areaColor = (name) => {
    const cat = categories.find((c) => c.name === name);
    return cat?.color || getArea(name).hex;
  };

  const reloadCategories = async () => {
    await loadCategories();
    const { data } = await api.get("/tasks");
    setTasks(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tasks", form);
      toast.success("Ev ödevi eklendi");
      setOpen(false);
      setForm(emptyTask);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDel = async () => {
    try {
      await api.delete(`/tasks/${del.id}`);
      toast.success("Görev silindi");
      setDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openAssign = (t) => {
    setAssignFor(t);
    setAForm({ student_id: students[0]?.id || "", frequency: "Her Gün", start_date: "", end_date: "", note: "" });
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!aForm.student_id) { toast.error("Öğrenci seç"); return; }
    setAssigning(true);
    try {
      await api.post("/assignments", { task_id: assignFor.id, ...aForm });
      toast.success("Görev veliye atandı");
      setAssignFor(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAssigning(false);
    }
  };

  const filterChips = [[ALL, "Tümü"], ...categories.map((c) => [c.name, c.name])];

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="tasks-page">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Aile Ev Ödevi</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Aile Ev Ödevi</h1>
          <p className="mt-2 text-sm text-slate-600">Şablon ev ödevleri oluştur, velilere ata ve kategorileri yönet.</p>
        </header>

        <Tabs defaultValue="odevler" className="w-full">
          <TabsList className="mb-6 rounded-xl bg-slate-100">
            <TabsTrigger value="odevler" data-testid="tab-tasks" className="rounded-lg data-[state=active]:bg-white">Ödevler</TabsTrigger>
            <TabsTrigger value="kategoriler" data-testid="tab-task-categories" className="rounded-lg data-[state=active]:bg-white">Kategoriler</TabsTrigger>
            <TabsTrigger value="verilen" data-testid="tab-assigned-tasks" className="rounded-lg data-[state=active]:bg-white">Verilen Ödevler</TabsTrigger>
          </TabsList>

          <TabsContent value="odevler">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
              <p className="text-sm text-slate-600">Şablon ödevleri listele ve veliye ata.</p>
              <Button onClick={() => { setForm(emptyTask); setOpen(true); }} data-testid="add-task-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700">
                <Plus className="mr-1.5 h-4 w-4" /> Görev Ekle
              </Button>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : tasks.length === 0 ? (
              <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="tasks-empty">
                <LayoutGrid className="mx-auto mb-3 h-10 w-10 text-slate-300" />Henüz ev ödevi yok. “Görev Ekle” ile başla.
              </Card>
            ) : (
              <>
                <div className="mb-6 flex flex-wrap gap-2">
                  {filterChips.map(([k, label]) => {
                    const active = areaFilter === k;
                    const c = k === ALL ? "#7C3AED" : areaColor(k);
                    return (
                      <button
                        key={k}
                        onClick={() => setAreaFilter(k)}
                        data-testid={`task-cat-${k}`}
                        className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                        style={active ? { backgroundColor: c, borderColor: c } : {}}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {tasks.filter((t) => areaFilter === ALL || t.area === areaFilter).map((t) => {
                    const hex = areaColor(t.area);
                    const meta = getArea(t.area);
                    return (
                      <Card key={t.id} data-testid={`task-card-${t.id}`} className="overflow-hidden rounded-2xl border-slate-200/60 p-0 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-1" style={{ borderTop: `4px solid ${hex}` }}>
                        {t.image_url ? (
                          <img src={t.image_url} alt={t.title} className="h-40 w-full object-cover" />
                        ) : (
                          <div className="flex h-24 w-full items-center justify-center" style={{ backgroundColor: `${hex}14` }}>
                            <meta.icon className="h-10 w-10" style={{ color: hex }} />
                          </div>
                        )}
                        <div className="p-5">
                          <h3 className="font-heading text-base font-semibold text-slate-900">{t.title}</h3>
                          {t.description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{t.description}</p>}
                          <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            {t.area && <Badge className="rounded-full border-0 hover:opacity-90" style={{ backgroundColor: `${hex}1f`, color: hex }}>{t.area}</Badge>}
                            <Badge className={`rounded-full ${DIFF[t.difficulty] || DIFF.Kolay}`}>{t.difficulty}</Badge>
                            {t.duration && <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" />{t.duration}</span>}
                          </div>
                          <div className="mt-4 flex gap-2">
                            <Button size="sm" onClick={() => openAssign(t)} data-testid={`assign-task-${t.id}`} className="flex-1 rounded-lg text-white hover:opacity-90" style={{ backgroundColor: hex }}>
                              <Send className="mr-1 h-3.5 w-3.5" /> Veliye Ata
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDel(t)} data-testid={`delete-task-${t.id}`} className="h-9 w-9 rounded-lg text-red-600 hover:bg-red-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="kategoriler">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : (
              <TaskCategoryManager categories={categories} reload={reloadCategories} />
            )}
          </TabsContent>

          <TabsContent value="verilen">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : (
              <AssignedTasksPanel students={students} areaColor={areaColor} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Yeni Görev</DialogTitle>
            <DialogDescription>Aile ev ödevi şablonu ekle.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2"><Label>Başlık *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
            <div className="space-y-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select value={form.area || undefined} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger data-testid="task-area-select"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zorluk</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger data-testid="task-difficulty-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Kolay">Kolay</SelectItem>
                    <SelectItem value="Orta">Orta</SelectItem>
                    <SelectItem value="Zor">Zor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Süre</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Örn. 5-10 dk" data-testid="task-duration-input" /></div>
              <div className="space-y-2"><Label>Görsel URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="task-image-input" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="task-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Görevi Ata</DialogTitle>
            <DialogDescription>{assignFor?.title} — veliye haftalık görev.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAssign} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Öğrenci (Veli) *</Label>
              <Select value={aForm.student_id} onValueChange={(v) => setAForm({ ...aForm, student_id: v })}>
                <SelectTrigger data-testid="assign-student-select"><SelectValue placeholder="Öğrenci seç" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.full_name}{s.parent_name ? ` — ${s.parent_name}` : " (veli yok)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Başlangıç</Label><Input type="date" value={aForm.start_date} onChange={(e) => setAForm({ ...aForm, start_date: e.target.value })} data-testid="assign-start-input" /></div>
              <div className="space-y-2"><Label>Bitiş</Label><Input type="date" value={aForm.end_date} onChange={(e) => setAForm({ ...aForm, end_date: e.target.value })} data-testid="assign-end-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Tekrar Sıklığı</Label>
              <Select value={aForm.frequency} onValueChange={(v) => setAForm({ ...aForm, frequency: v })}>
                <SelectTrigger data-testid="assign-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Her Gün">Her Gün</SelectItem>
                  <SelectItem value="Haftalık">Haftalık</SelectItem>
                  <SelectItem value="Tek Sefer">Tek Sefer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Aile Notu</Label><Textarea value={aForm.note} onChange={(e) => setAForm({ ...aForm, note: e.target.value })} data-testid="assign-note-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignFor(null)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={assigning} data-testid="assign-submit-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Görevi Ata"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-task-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Görevi sil?</AlertDialogTitle>
            <AlertDialogDescription><strong>{del?.title}</strong> silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDel} data-testid="confirm-delete-task" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
